import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { FileDown, Loader2, Search, CheckCircle2, XCircle, Eye, X, AlertTriangle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { placementDocsAPI } from '../lib/api';
import { usePagination, PaginationBar } from '../components/PaginationBar';

// Server-side hard cap. Must match backend `_EXPORT_MAX_PLACEMENTS`.
const MAX_PLACEMENTS = 500;

const StatusPill = ({ status }) => {
  const s = (status || 'not_created').toLowerCase();
  const map = {
    signed: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2, label: 'Signed' },
    viewed: { cls: 'bg-sky-100 text-sky-800 border-sky-200', Icon: CheckCircle2, label: 'Viewed' },
    sent: { cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: CheckCircle2, label: 'Sent' },
    draft: { cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: CheckCircle2, label: 'Draft' },
    voided: { cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle, label: 'Voided' },
    not_created: { cls: 'bg-white text-gray-400 border-dashed border-gray-300', Icon: XCircle, label: '—' },
  };
  const cfg = map[s] || map.not_created;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const formatBytes = (n) => {
  if (!n || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
};

/**
 * CycleExports — admin-only page for downloading a ZIP of signed
 * ETAs, Placement Confirmation Letters and Industry Placement Confirmation
 * Letters across a set of hand-picked placements.
 *
 * The job-based flow (`start → poll → download`) uses a MongoDB-backed job
 * store, so a page reload or backend restart no longer strands the user.
 */
const CycleExports = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [includeETA, setIncludeETA] = useState(false);
  const [includePlacement, setIncludePlacement] = useState(true);
  const [includeIndustry, setIncludeIndustry] = useState(true);

  // Export state — one job at a time (matches server ownership model).
  // Shape: { id, status, total, done, included, skipped, failed_files, filename, size_bytes, phase, dl }
  //   phase: 'rendering' | 'downloading' | null
  //   dl: { loaded, total }
  const [job, setJob] = useState(null);
  // Resumable jobs from a previous session — surfaced as a banner.
  const [resumable, setResumable] = useState([]);
  // Cancellation is optimistic on the client side.
  const cancellingRef = useRef(false);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      reload();
      loadActiveJobs();
    }
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [user]);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await placementDocsAPI.overview();
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load placements');
    } finally {
      setLoading(false);
    }
  };

  // On mount, ask the server if this admin has any in-flight jobs so we can
  // show a "resume" banner instead of stranding them after a refresh.
  const loadActiveJobs = async () => {
    try {
      const r = await placementDocsAPI.activeExports();
      const jobs = (r.data?.jobs || []).filter((j) => j.status === 'pending' || j.status === 'running' || j.status === 'complete');
      setResumable(jobs);
    } catch {
      /* silent — this is optional UX polish */
    }
  };

  const hasSelectedSigned = (row) => {
    if (includeETA && row.documents.eta.status === 'signed') return true;
    if (includePlacement && row.documents.placement_letter.status === 'signed') return true;
    if (includeIndustry && row.documents.industry_letter.status === 'signed') return true;
    return false;
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      (r.student_name || '').toLowerCase().includes(query) ||
      (r.student_number || '').toLowerCase().includes(query) ||
      (r.restaurant_name || '').toLowerCase().includes(query) ||
      (r.position || '').toLowerCase().includes(query)
    );
  }, [rows, q]);
  const pg = usePagination(filtered, 'cycle-exports');

  const eligibleFiltered = useMemo(
    () => filtered.filter(hasSelectedSigned),
    [filtered, includeETA, includePlacement, includeIndustry]
  );
  const allEligibleSelected = eligibleFiltered.length > 0 && eligibleFiltered.every((r) => selected.has(r.placement_id));

  const toggleAll = () => {
    const s = new Set(selected);
    if (allEligibleSelected) {
      eligibleFiltered.forEach((r) => s.delete(r.placement_id));
    } else {
      // Respect the server-side cap so we don't ambush the user with a 400.
      const room = MAX_PLACEMENTS - s.size;
      const toAdd = eligibleFiltered.filter((r) => !s.has(r.placement_id)).slice(0, Math.max(0, room));
      toAdd.forEach((r) => s.add(r.placement_id));
      if (eligibleFiltered.length > toAdd.length + [...s].filter((id) => eligibleFiltered.find((r) => r.placement_id === id)).length) {
        toast.warning(`Selection capped at ${MAX_PLACEMENTS} placements per export.`);
      }
    }
    setSelected(s);
  };

  const toggleRow = (id) => {
    const s = new Set(selected);
    if (s.has(id)) {
      s.delete(id);
    } else {
      if (s.size >= MAX_PLACEMENTS) {
        toast.warning(`Maximum ${MAX_PLACEMENTS} placements per export.`);
        return;
      }
      s.add(id);
    }
    setSelected(s);
  };

  const anyCategory = includeETA || includePlacement || includeIndustry;
  const canExport = selected.size > 0 && anyCategory && !job;

  const handlePreview = async (contractId, e) => {
    if (e) e.stopPropagation();
    if (!contractId) return;
    const t = toast.loading('Rendering preview…');
    try {
      const res = await placementDocsAPI.previewContract(contractId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.dismiss(t);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  // Download the completed ZIP with proper progress tracking.
  const downloadReadyJob = async (jobRef) => {
    setJob({ ...jobRef, phase: 'downloading', dl: { loaded: 0, total: jobRef.size_bytes || 0 } });
    try {
      const dl = await placementDocsAPI.downloadExport(jobRef.job_id || jobRef.id, (evt) => {
        setJob((cur) => (cur ? { ...cur, dl: { loaded: evt.loaded, total: evt.total || jobRef.size_bytes || 0 } } : cur));
      });
      const blob = new Blob([dl.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = jobRef.filename || 'cycle-export.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const failedCount = (jobRef.failed_files || []).length;
      const included = jobRef.included ?? 0;
      const skipped = jobRef.skipped ?? 0;
      let msg = `Exported ${included} PDF${included === 1 ? '' : 's'}`;
      if (skipped) msg += ` — ${skipped} skipped (no signed doc)`;
      if (failedCount) msg += ` — ${failedCount} failed (see _errors.txt in ZIP)`;
      if (failedCount) toast.warning(msg); else toast.success(msg);
      setJob(null);
      // Refresh resumable list — this one is done.
      loadActiveJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
      setJob(null);
    }
  };

  const startPolling = (jobId, initialFilename) => {
    cancellingRef.current = false;
    let delay = 500;         // start with 500ms
    const MAX_DELAY = 3000;  // cap at 3s
    let consecutiveErrors = 0;

    const poll = async () => {
      try {
        const s = await placementDocsAPI.exportStatus(jobId);
        consecutiveErrors = 0;
        delay = 500;
        const st = s.data;
        setJob((cur) => ({ ...(cur || {}), ...st, id: jobId, filename: st.filename || initialFilename, phase: 'rendering' }));
        if (st.status === 'complete') {
          await downloadReadyJob(st);
          return;
        }
        if (st.status === 'failed') {
          toast.error(st.error || 'Export failed');
          setJob(null);
          return;
        }
        if (st.status === 'cancelled') {
          toast.info('Export cancelled.');
          setJob(null);
          return;
        }
        pollTimerRef.current = setTimeout(poll, delay);
      } catch (err) {
        consecutiveErrors += 1;
        // Exponential backoff on transient network errors, cap at MAX_DELAY.
        delay = Math.min(MAX_DELAY, delay * 2);
        if (consecutiveErrors >= 6) {
          toast.error('Lost connection to the export job. Please refresh.');
          setJob(null);
          return;
        }
        pollTimerRef.current = setTimeout(poll, delay);
      }
    };
    pollTimerRef.current = setTimeout(poll, 400);
  };

  const handleExport = async () => {
    if (!canExport) return;
    const categories = [];
    if (includeETA) categories.push('establishment');
    if (includePlacement) categories.push('placement');
    if (includeIndustry) categories.push('placement_industry');

    try {
      const startRes = await placementDocsAPI.startExport({
        placement_ids: Array.from(selected),
        categories,
      });
      const { job_id, total, skipped, filename } = startRes.data;
      setJob({ id: job_id, status: 'running', total, done: 0, filename, skipped, failed_files: [], phase: 'rendering' });
      startPolling(job_id, filename);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start export');
      setJob(null);
    }
  };

  const handleCancel = async () => {
    if (!job || cancellingRef.current) return;
    cancellingRef.current = true;
    try {
      await placementDocsAPI.cancelExport(job.id);
      toast.info('Cancellation requested — stopping after the current PDF.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
      cancellingRef.current = false;
    }
  };

  const handleResume = async (jobFromList) => {
    if (job) return; // already busy
    if (jobFromList.status === 'complete') {
      await downloadReadyJob(jobFromList);
    } else {
      setJob({ ...jobFromList, id: jobFromList.job_id, phase: 'rendering' });
      startPolling(jobFromList.job_id, jobFromList.filename);
    }
    setResumable((cur) => cur.filter((j) => j.job_id !== jobFromList.job_id));
  };

  const handleDismissResumable = (jobId) => {
    setResumable((cur) => cur.filter((j) => j.job_id !== jobId));
  };

  // ---- render helpers ----
  const renderProgressCard = () => {
    if (!job) return null;
    const total = job.total || 1;
    const pctRender = Math.min(100, Math.round((job.done / total) * 100));
    const dlTotal = job.dl?.total || 0;
    const dlLoaded = job.dl?.loaded || 0;
    const pctDl = dlTotal > 0 ? Math.min(100, Math.round((dlLoaded / dlTotal) * 100)) : 0;
    const isDownloading = job.phase === 'downloading';
    const failedCount = (job.failed_files || []).length;

    return (
      <Card data-testid="export-progress" className="border-ica-navy/40">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-ica-navy">
              {isDownloading ? (
                <>
                  <Download className="w-4 h-4" />
                  Downloading ZIP{dlTotal ? ` (${formatBytes(dlLoaded)} / ${formatBytes(dlTotal)})` : '…'}
                </>
              ) : (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {job.cancel_requested ? 'Cancelling…' : `Rendering ${job.done} of ${job.total} PDFs…`}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-500">
                {isDownloading ? `${pctDl}%` : `${pctRender}%`}
              </div>
              {!isDownloading && !job.cancel_requested && (
                <Button
                  data-testid="cancel-export-btn"
                  onClick={handleCancel}
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              )}
            </div>
          </div>
          <Progress value={isDownloading ? pctDl : pctRender} data-testid="export-progress-bar" />
          {failedCount > 0 && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              <AlertTriangle className="w-3 h-3" />
              {failedCount} PDF{failedCount === 1 ? '' : 's'} failed to render and will be listed in <code className="mx-1">_errors.txt</code> inside the ZIP.
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      {user && user.role !== 'admin' ? (
        <Navigate to="/dashboard" replace />
      ) : (
      <div className="space-y-6" data-testid="cycle-exports-page">
        <div>
          <h1 className="text-3xl font-bold text-ica-navy">Cycle Exports</h1>
          <p className="text-gray-500 mt-1">
            Download a ZIP of signed <strong>ETAs</strong>,{' '}
            <strong>Student Placement Confirmation Letters</strong> and{' '}
            <strong>Industry Placement Confirmation Letters</strong> for the placements you select.
            Only <em>signed</em> contracts are included. Files are grouped in a dated folder inside the ZIP.
            Max {MAX_PLACEMENTS} placements per export.
          </p>
        </div>

        {/* Resumable jobs banner — visible if a previous session left something in flight */}
        {resumable.length > 0 && !job && (
          <div className="space-y-2" data-testid="resumable-jobs">
            {resumable.map((rj) => (
              <div
                key={rj.job_id}
                className="flex items-center justify-between border border-ica-navy/30 bg-ica-navy/5 rounded-lg px-4 py-3"
                data-testid={`resumable-${rj.job_id}`}
              >
                <div className="text-sm">
                  <div className="font-medium text-ica-navy">
                    {rj.status === 'complete' ? 'Ready to download' : 'Export still running'}
                    : {rj.filename}
                  </div>
                  <div className="text-xs text-gray-500">
                    {rj.done}/{rj.total} PDFs rendered
                    {rj.size_bytes ? ` · ${formatBytes(rj.size_bytes)}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    
                    onClick={() => handleResume(rj)}
                    data-testid={`resume-${rj.job_id}`}
                  >
                    {rj.status === 'complete' ? (
                      <><Download className="w-3 h-3 mr-1" /> Download</>
                    ) : (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Resume</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDismissResumable(rj.job_id)}
                    data-testid={`dismiss-${rj.job_id}`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">1. Choose documents to include</CardTitle>
            <CardDescription>Tick the letters you want inside the ZIP.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-eta">
              <Checkbox checked={includeETA} onCheckedChange={(v) => setIncludeETA(!!v)} />
              <span className="text-sm">Experiential Training Agreement <span className="text-gray-500">(per restaurant, deduped)</span></span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-placement-letter">
              <Checkbox checked={includePlacement} onCheckedChange={(v) => setIncludePlacement(!!v)} />
              <span className="text-sm">Student Placement Confirmation Letter <span className="text-gray-500">(per student)</span></span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-industry-letter">
              <Checkbox checked={includeIndustry} onCheckedChange={(v) => setIncludeIndustry(!!v)} />
              <span className="text-sm">Industry Placement Confirmation Letter <span className="text-gray-500">(per restaurant, deduped)</span></span>
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              2. Select placements
              <span className="text-xs text-gray-500 font-normal">
                {eligibleFiltered.length} eligible (has signed doc) · {selected.size} selected
              </span>
            </CardTitle>
            <CardDescription>Rows with no signed doc in the selected categories are greyed out.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by student, restaurant, position…"
                  className="pl-9"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={toggleAll}
                disabled={eligibleFiltered.length === 0}
                data-testid="select-all-btn"
              >
                {allEligibleSelected ? 'Clear all' : `Select all (${Math.min(MAX_PLACEMENTS, eligibleFiltered.length)})`}
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-ica-navy" /></div>
            ) : (
              <div className="border rounded-lg">
                <PaginationBar {...pg.bar} position="top" noun="placements" className="rounded-t-lg" />
                <div className="table-scroll">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left"></th>
                      <th className="text-left py-2 px-3">Student</th>
                      <th className="text-left py-2 px-3">Restaurant</th>
                      <th className="text-left py-2 px-3">Period</th>
                      <th className="text-left py-2 px-3">ETA</th>
                      <th className="text-left py-2 px-3">Student Placement Letter</th>
                      <th className="text-left py-2 px-3">Industry Letter</th>
                      <th className="w-16 text-right px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((r) => {
                      const eligible = hasSelectedSigned(r);
                      const checked = selected.has(r.placement_id);
                      const previewId = (
                        (includePlacement && r.documents.placement_letter.status === 'signed' && r.documents.placement_letter.instance_id) ||
                        (includeIndustry && r.documents.industry_letter.status === 'signed' && r.documents.industry_letter.instance_id) ||
                        (includeETA && r.documents.eta.status === 'signed' && r.documents.eta.instance_id) ||
                        null
                      );
                      return (
                        <tr
                          key={r.placement_id}
                          data-testid={`row-${r.placement_id}`}
                          className={`border-t ${eligible ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50'}`}
                          onClick={() => eligible && toggleRow(r.placement_id)}
                        >
                          <td className="px-3 py-2">
                            <Checkbox
                              data-testid={`checkbox-${r.placement_id}`}
                              checked={checked}
                              disabled={!eligible}
                              onCheckedChange={() => eligible && toggleRow(r.placement_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="py-2 px-3">
                            <div className="font-medium text-gray-800">{r.student_name}</div>
                            <div className="text-xs text-gray-500">{r.student_number} · {r.student_year}</div>
                          </td>
                          <td className="py-2 px-3">{r.restaurant_name}</td>
                          <td className="py-2 px-3 text-gray-600">{r.start_date} → {r.end_date}</td>
                          <td className="py-2 px-3"><StatusPill status={r.documents.eta.status} /></td>
                          <td className="py-2 px-3"><StatusPill status={r.documents.placement_letter.status} /></td>
                          <td className="py-2 px-3"><StatusPill status={r.documents.industry_letter.status} /></td>
                          <td className="py-2 px-3 text-right">
                            {previewId ? (
                              <button
                                type="button"
                                title="Preview PDF"
                                data-testid={`preview-${r.placement_id}`}
                                onClick={(e) => handlePreview(previewId, e)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-ica-navy/10 text-ica-navy transition"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-gray-400">No placements match your search.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
                <PaginationBar {...pg.bar} position="bottom" noun="placements" className="rounded-b-lg" />
              </div>
            )}
          </CardContent>
        </Card>

        {renderProgressCard()}

        <div className="flex justify-end sticky bottom-4">
          <Button
            data-testid="export-btn"
            onClick={handleExport}
            disabled={!canExport}
            className="shadow-lg"
            size="lg"
          >
            {job ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing ZIP…</>
            ) : (
              <><FileDown className="w-4 h-4 mr-2" /> Download ZIP ({selected.size} placement{selected.size === 1 ? '' : 's'})</>
            )}
          </Button>
        </div>
      </div>
      )}
    </Layout>
  );
};

export default CycleExports;
